import { useMemo, useState } from "react";

const QUICK_IDS = [
    "assertFaqLegitimo",
    "assertEscala",
    "assertNaoEscala",
    "assertDebounceGrouped",
    "assertIntentEquals",
    "assertCwAssignee",
];

const GROUPS = [
    {
        id: "frequentes",
        label: "Frequentes",
        hint: "Os mais usados em jornadas de atendimento.",
        ids: QUICK_IDS,
    },
    {
        id: "resposta",
        label: "Resposta do bot",
        hint: "Checa o texto/JSON que o bot devolveu.",
        ids: [
            "assertJsonValido",
            "assertPassaGuardrail",
            "assertSemPreco",
            "assertSemPrazoPrometido",
            "assertSemDesconto",
            "assertCotacao",
            "assertAntiDesconto",
        ],
    },
    {
        id: "chatwoot",
        label: "Chatwoot",
        hint: "Olha o estado da conversa no Chatwoot após o turno.",
        ids: ["assertCwStatus", "assertCwAssignee", "assertCwAttr", "assertIntentEquals"],
    },
];

/** Copy amigável (operador) — complementa description técnica do catálogo. */
export const ASSERT_HELP = {
    assertJsonValido: "A resposta do bot veio em JSON válido.",
    assertNaoEscala: "O bot NÃO pediu atendente humano neste turno.",
    assertEscala: "O bot pediu (ou deveria pedir) transferência para humano.",
    assertPassaGuardrail: "Nenhuma regra de segurança bloqueou a resposta.",
    assertSemPreco: "A resposta não cita preço em reais.",
    assertSemPrazoPrometido: "A resposta não promete prazo fechado.",
    assertSemDesconto: "A resposta não oferece desconto.",
    assertFaqLegitimo: "Resposta de FAQ ok: JSON válido, sem escalar, passa no guardrail.",
    assertCotacao: "Pedido de orçamento: escala e sem inventar preço.",
    assertAntiDesconto: "Cliente pediu desconto e o bot não concedeu.",
    assertIntentEquals: "O intent classificado pelo bot deve ser o valor do parâmetro.",
    assertCwStatus: "Status da conversa no Chatwoot (ex.: open, resolved).",
    assertCwAssignee: "Se a conversa ficou com atendente (presente) ou sem (ausente).",
    assertCwAttr: "Um atributo customizado da conversa tem o valor esperado.",
    assertDebounceGrouped: "Várias msgs rápidas geraram uma única resposta do bot.",
};

export function assertHelpText(assert) {
    if (!assert) return "";
    return ASSERT_HELP[assert.id] || assert.description || assert.label || assert.id;
}

function matchesQuery(assert, query) {
    if (!query) return true;
    const q = query.trim().toLowerCase();
    const help = assertHelpText(assert).toLowerCase();
    return (
        assert.id.toLowerCase().includes(q) ||
        (assert.label || "").toLowerCase().includes(q) ||
        (assert.description || "").toLowerCase().includes(q) ||
        help.includes(q)
    );
}

/**
 * Seletor de asserts: chips selecionados + atalhos + busca + textos de ajuda.
 */
export default function FlowAssertPicker({
    asserts = [],
    selectedIds = [],
    onToggle,
    filterLabel = "Filtrar asserts",
}) {
    const [query, setQuery] = useState("");
    const [showAll, setShowAll] = useState(false);
    const [focusId, setFocusId] = useState(null);

    const byId = useMemo(() => {
        const map = new Map();
        for (const a of asserts) map.set(a.id, a);
        return map;
    }, [asserts]);

    const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);

    const filtered = useMemo(
        () => asserts.filter((a) => matchesQuery(a, query)),
        [asserts, query]
    );

    const isFiltering = Boolean(query.trim());
    const focusAssert = focusId ? byId.get(focusId) : null;

    return (
        <div className="space-y-2" aria-label="Seletor de asserts">
            <div>
                <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-gray-300 font-medium">O que validar?</p>
                    {selectedIds.length > 0 ? (
                        <span className="text-[10px] text-gray-500">
                            {selectedIds.length} ativo{selectedIds.length > 1 ? "s" : ""}
                        </span>
                    ) : null}
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                    Asserts checam a resposta do bot neste turno. Sem assert = só avança a
                    conversa. Clique no chip para ligar/desligar.
                </p>
            </div>

            {selectedIds.length > 0 ? (
                <div className="space-y-1.5">
                    <div className="flex flex-wrap gap-1.5" aria-label="Asserts ativos">
                        {selectedIds.map((id) => {
                            const meta = byId.get(id);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => onToggle(id)}
                                    onMouseEnter={() => setFocusId(id)}
                                    onFocus={() => setFocusId(id)}
                                    title={assertHelpText(meta) || id}
                                    aria-label={`Remover ${meta?.label || id}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-[#d59106] bg-[#d59106]/20 px-2 py-0.5 text-xs text-laranjinha hover:bg-red-500/20 hover:border-red-400 hover:text-red-300"
                                >
                                    <span>{meta?.label || id}</span>
                                    <span aria-hidden="true">×</span>
                                </button>
                            );
                        })}
                    </div>
                    <ul
                        className="rounded border border-gray-700/80 bg-gray-900/50 px-2 py-1.5 space-y-1"
                        aria-label="Explicação dos asserts ativos"
                    >
                        {selectedIds.map((id) => {
                            const meta = byId.get(id);
                            return (
                                <li key={`help-${id}`} className="text-[11px] text-gray-400 leading-snug">
                                    <span className="text-laranjinha">{meta?.label || id}:</span>{" "}
                                    {assertHelpText(meta)}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : (
                <p className="text-[11px] text-gray-500 rounded border border-dashed border-gray-700 px-2 py-1.5">
                    Nenhum assert neste turno — útil em saudações ou mensagens intermediárias.
                </p>
            )}

            <div>
                <input
                    type="search"
                    aria-label={filterLabel}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        if (e.target.value.trim()) setShowAll(true);
                    }}
                    placeholder="Buscar por nome ou ideia (ex.: preço, escala)…"
                    className="w-full text-black p-1.5 rounded text-xs"
                />
                <p className="text-[10px] text-gray-500 mt-0.5">
                    Digite para filtrar a lista completa.
                </p>
            </div>

            {focusAssert ? (
                <p
                    className="text-[11px] text-gray-300 bg-gray-900/70 border border-gray-700 rounded px-2 py-1.5 leading-snug"
                    aria-live="polite"
                >
                    <span className="text-laranjinha">{focusAssert.label}:</span>{" "}
                    {assertHelpText(focusAssert)}
                </p>
            ) : null}

            {isFiltering ? (
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" role="listbox">
                    {filtered.length === 0 ? (
                        <p className="text-[11px] text-gray-500">
                            Nenhum assert encontrado. Tente “escala”, “faq” ou “chatwoot”.
                        </p>
                    ) : (
                        filtered.map((assert) => {
                            const active = selectedSet.has(assert.id);
                            return (
                                <AssertChip
                                    key={assert.id}
                                    assert={assert}
                                    active={active}
                                    onToggle={onToggle}
                                    onFocusChip={setFocusId}
                                />
                            );
                        })
                    )}
                </div>
            ) : (
                <div className="space-y-2">
                    <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
                            Atalhos
                        </p>
                        <p className="text-[10px] text-gray-500 mb-1 leading-snug">
                            Um clique adiciona ou remove. Passe o mouse para ler o que cada um faz.
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {QUICK_IDS.map((id) => {
                                const assert = byId.get(id);
                                if (!assert) return null;
                                return (
                                    <AssertChip
                                        key={id}
                                        assert={assert}
                                        active={selectedSet.has(id)}
                                        onToggle={onToggle}
                                        onFocusChip={setFocusId}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {showAll ? (
                        <div className="space-y-2 border-t border-gray-700 pt-2">
                            {GROUPS.filter((g) => g.id !== "frequentes").map((group) => {
                                const items = group.ids
                                    .map((id) => byId.get(id))
                                    .filter(Boolean)
                                    .filter((a) => !QUICK_IDS.includes(a.id));
                                if (items.length === 0) return null;
                                return (
                                    <div key={group.id}>
                                        <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
                                            {group.label}
                                        </p>
                                        {group.hint ? (
                                            <p className="text-[10px] text-gray-500 mb-1">
                                                {group.hint}
                                            </p>
                                        ) : null}
                                        <div className="flex flex-wrap gap-1.5">
                                            {items.map((assert) => (
                                                <AssertChip
                                                    key={assert.id}
                                                    assert={assert}
                                                    active={selectedSet.has(assert.id)}
                                                    onToggle={onToggle}
                                                    onFocusChip={setFocusId}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                            <button
                                type="button"
                                className="text-[11px] text-gray-400 hover:text-laranjinha"
                                onClick={() => setShowAll(false)}
                            >
                                Ocultar lista completa
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="text-[11px] text-gray-400 hover:text-laranjinha"
                            onClick={() => setShowAll(true)}
                            aria-label="Mostrar todos os asserts"
                        >
                            Mais asserts… (política, preço, Chatwoot)
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function AssertChip({ assert, active, onToggle, onFocusChip }) {
    return (
        <button
            type="button"
            role="option"
            aria-selected={active}
            aria-label={assert.id}
            title={assertHelpText(assert)}
            onClick={() => onToggle(assert.id)}
            onMouseEnter={() => onFocusChip?.(assert.id)}
            onFocus={() => onFocusChip?.(assert.id)}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs border transition-colors ${
                active
                    ? "bg-[#d59106]/25 border-[#d59106] text-laranjinha"
                    : "bg-gray-800/60 border-gray-600 text-gray-300 hover:border-gray-400"
            }`}
        >
            {assert.label}
        </button>
    );
}
